import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { ContainerInventory as Dati } from '../api/types'
import { CategoryBars, Donut, formatBytes } from '../components/charts'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { colonneContainer } from '../components/Container'
import { DataTable } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Inventario container: the containers the CENSO agents find on their hosts
 * (Docker, Podman, containerd, CRI-O — Kubernetes, k3s, RKE2), beside the
 * virtual and the cloud inventories. Tabs: «Panoramica», «Container»,
 * «Immagini», «Kubernetes»; tables filter and sort per column. The content of
 * the containers (distribution, packages, libraries) is the SBOM of their
 * image. See docs/architecture/containers.md.
 */

type Vista = 'panoramica' | 'container' | 'immagini' | 'kubernetes'

const VISTE: Array<{ key: Vista; label: () => string }> = [
  { key: 'panoramica', label: () => t('Panoramica') },
  { key: 'container', label: () => t('Container') },
  { key: 'immagini', label: () => t('Immagini') },
  { key: 'kubernetes', label: () => t('Kubernetes') },
]

const n = (x: number) => x.toLocaleString(localeCorrente())

export default function ContainerInventory() {
  const [params, setParams] = useSearchParams()
  const vista = (VISTE.find((v) => v.key === params.get('vista'))?.key ?? 'panoramica') as Vista
  const dati = useAsync(() => api.containerInventory(), [])
  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Inventario container')}</h1>
          <p className="page__subtitle">
            {t('inventario rilevato dagli agenti CENSO sui runtime Docker, Podman, containerd e CRI-O, inclusi cluster Kubernetes, k3s e RKE2, con immagine, stato, porte, pod e namespace. L’analisi SBOM identifica distribuzione, pacchetti di sistema e librerie delle immagini in esecuzione e ne valuta le vulnerabilità, escludendo dalla raccolta variabili d’ambiente e command line.')}
          </p>
        </div>
      </header>
      <nav className="tabs" role="tablist">
        {VISTE.map((v) => (
          <button key={v.key} role="tab" aria-selected={vista === v.key}
            className={`tabs__tab${vista === v.key ? ' tabs__tab--active' : ''}`}
            onClick={() => setParams(v.key === 'panoramica' ? {} : { vista: v.key })}>
            {v.label()}
          </button>
        ))}
      </nav>
      <ErrorBanner error={dati.error} />
      {dati.loading && !dati.data ? <Spinner /> : !dati.data || dati.data.containers.length === 0 ? (
        <Card>
          <EmptyState title={t('Nessun container ancora')}
            hint={t('I container si leggono dall’agente CENSO installato sugli host che li eseguono, come root: alla prossima raccolta completa compaiono qui. Il contenuto delle immagini richiede l’opzione «SBOM» dell’agente.')} />
        </Card>
      ) : (
        <>
          {vista === 'panoramica' && <Panoramica dati={dati.data} vai={(v) => setParams({ vista: v })} />}
          {vista === 'container' && (
            <Card>
              <DataTable rows={dati.data.containers} rowKey={(c) => c.id} columns={colonneContainer(true)}
                initialSort={{ key: 'host' }} conteggio={{ nome: t('container') }}
                paginazione={{ predefinita: 50, opzioni: [25, 50, 100, 250] }} />
            </Card>
          )}
          {vista === 'immagini' && <Immagini dati={dati.data} />}
          {vista === 'kubernetes' && <Kubernetes dati={dati.data} />}
        </>
      )}
    </>
  )
}

function Panoramica({ dati, vai }: { dati: Dati; vai: (v: Vista) => void }) {
  const s = dati.stats
  const tile = (valore: number, etichetta: string, v?: Vista, tono?: string, hint?: string) => {
    const corpo = (
      <>
        <div className="stat__value">{n(valore)}</div>
        <div className="stat__label">{etichetta}</div>
        {hint && <div className="stat__hint">{hint}</div>}
      </>
    )
    const cls = `stat${tono ? ` stat--${tono}` : ''}`
    return v ? <button className={cls} onClick={() => vai(v)}>{corpo}</button> : <div className={cls}>{corpo}</div>
  }
  return (
    <>
      <div className="stats">
        {tile(s.hosts, t('Host'))}
        {tile(s.containers, t('Container'), 'container', undefined, t('{a} in esecuzione, {b} fermi', { a: n(s.running), b: n(s.stopped) }))}
        {tile(s.images, t('Immagini'), 'immagini', undefined, t('{n} non usate', { n: n(s.unused_images) }))}
        {tile(s.namespaces, t('Namespace Kubernetes'), 'kubernetes', undefined, t('{n} pod', { n: n(s.pods) }))}
        {tile(s.compose_projects, t('Progetti Compose'))}
        {tile(s.with_content, t('Con contenuto letto'), undefined, undefined, t('richiede l’opzione SBOM'))}
        {tile(s.vulnerable_containers, t('Container con pacchetti vulnerabili'), 'container', s.vulnerable_containers ? 'warn' : 'ok')}
      </div>
      <div className="grid grid--2">
        <Card title={t('Stato dei container')}>
          <Donut centreLabel={t('container')} slices={[
            { key: 'running', label: t('in esecuzione'), value: s.running, color: 'var(--ok)' },
            { key: 'stopped', label: t('fermi'), value: s.stopped, color: 'var(--text-muted)' },
          ]} onSliceClick={() => vai('container')} />
        </Card>
        <Card title={t('Runtime')}>
          <CategoryBars data={Object.entries(s.runtimes).map(([k, v]) => ({ key: k, label: k, value: v }))} />
        </Card>
        <Card title={t('Sistemi nelle immagini')}>
          <CategoryBars data={Object.entries(s.operating_systems).map(([k, v]) => ({ key: k, label: k === '—' ? t('non letto') : k, value: v }))} />
        </Card>
        <Card title={t('Namespace con più container')}>
          <CategoryBars data={dati.kubernetes.slice(0, 10).map((k) => ({ key: k.namespace, label: k.namespace, value: k.containers }))}
            emptyHint={t('Nessun container Kubernetes.')} />
        </Card>
      </div>
      <Card title={t('Runtime per host')}>
        <DataTable rows={dati.runtimes} rowKey={(r) => r.host_ci_id + r.name} initialSort={{ key: 'host' }}
          columns={[
            { key: 'host', header: t('Host'), render: (r) => <Link to={`/cis/${r.host_ci_id}#container`}>{r.host}</Link> },
            { key: 'name', header: t('Runtime') },
            { key: 'version', header: t('Versione'), mono: true },
            { key: 'endpoint', header: t('Accesso'), mono: true },
          ]} />
      </Card>
    </>
  )
}

function Immagini({ dati }: { dati: Dati }) {
  return (
    <Card>
      <DataTable rows={dati.images} rowKey={(i) => i.id} initialSort={{ key: 'containers', dir: 'desc' }}
        conteggio={{ nome: t('immagini') }} paginazione={{ predefinita: 50 }}
        columns={[
          { key: 'tags', header: t('Immagine'), value: (i) => i.tags.join(', ') || i.image_id.slice(0, 19), mono: true },
          { key: 'host', header: t('Host'), render: (i) => <Link to={`/cis/${i.host_ci_id}#container`}>{i.host}</Link>, value: (i) => i.host },
          { key: 'runtime', header: t('Runtime') },
          { key: 'image_id', header: t('Identificativo'), value: (i) => i.image_id.replace('sha256:', '').slice(0, 12), mono: true },
          { key: 'size', header: t('Dimensione'), value: (i) => i.size, filter: false, render: (i) => formatBytes(i.size) },
          { key: 'containers', header: t('Container'), value: (i) => i.containers, filter: false,
            render: (i) => (i.unused ? <small className="text-muted">{t('non usata')}</small> : i.containers) },
          { key: 'created', header: t('Creata'), mono: true },
        ]} />
    </Card>
  )
}

function Kubernetes({ dati }: { dati: Dati }) {
  if (dati.kubernetes.length === 0) {
    return <Card><EmptyState title={t('Nessun container Kubernetes.')} hint={t('Sui nodi Kubernetes, k3s o RKE2 l’agente legge i container tramite crictl.')} /></Card>
  }
  return (
    <Card>
      <DataTable rows={dati.kubernetes} rowKey={(k) => k.namespace} initialSort={{ key: 'containers', dir: 'desc' }}
        conteggio={{ nome: t('namespace') }}
        columns={[
          { key: 'namespace', header: t('Namespace'), mono: true },
          { key: 'pods', header: t('Pod'), value: (k) => k.pods, filter: false },
          { key: 'containers', header: t('Container'), value: (k) => k.containers, filter: false },
          { key: 'running', header: t('In esecuzione'), value: (k) => k.running, filter: false },
          { key: 'images', header: t('Immagini'), value: (k) => k.images, filter: false },
          { key: 'hosts', header: t('Nodi'), value: (k) => k.hosts.join(', ') },
          { key: 'vulnerable', header: t('Componenti vulnerabili'), value: (k) => k.vulnerable, filter: false },
        ]} />
    </Card>
  )
}
